"use client";

import React, { useState } from "react";
import { Activity, ArrowUpRight, Award, CircleCheck as CheckCircle2, FileCheck2, FlaskConical, Gauge, Settings2, ShieldAlert, Wrench } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
interface ServiceItem {
  id: string;
  title: string;
  category: string;
  description: string;
  highlights: string;
  iconName: string;
  pagePath: string;
}
const servicesData: ServiceItem[] = [{
  id: "qual-val",
  title: "Equipment Qualification & Validation",
  category: "Compliance & Assurance",
  description: "Complete IQ, OQ, and PQ validation protocols tailored for regulated pharmaceutical, biopharma, and medical device environments.",
  highlights: "Full protocol execution & audit-ready reporting",
  iconName: "FileCheck2",
  pagePath: "/ServicesPage"
}, {
  id: "pat-tech",
  title: "Process Analytical Technology (PAT)",
  category: "In-Line Monitoring",
  description: "Real-time process monitoring solutions including blend uniformity, drying endpoints, and NIR spectral measurement systems.",
  highlights: "Continuous quality verification & cycle reduction",
  iconName: "Activity",
  pagePath: "/ProcessAnalyticalTechnologyPage"
}, {
  id: "preventative-maint",
  title: "Preventative Maintenance & Calibration",
  category: "Lifecycle Support",
  description: "Routine servicing, certified calibration, and OEM spare part replacements to maximize instrument uptime and measurement accuracy.",
  highlights: "Scheduled service plans with structured response process",
  iconName: "Wrench",
  pagePath: "/ServicesPage"
}, {
  id: "powder-testing",
  title: "Powder & Tablet Testing Support",
  category: "Physical Characterization",
  description: "Specialized characterization instruments for powder flow, bulk density, dissolution, friability, and tablet hardness testing.",
  highlights: "Pharmacopeia-compliant testing instrumentation",
  iconName: "FlaskConical",
  pagePath: "/PowderTestingPage"
}, {
  id: "raw-material-id",
  title: "Raw Material Identification & Security",
  category: "Quality Control",
  description: "Handheld and benchtop Raman and NIR verification systems for rapid warehouse incoming material release and safety workflows.",
  highlights: "Non-destructive testing through transparent packaging",
  iconName: "ShieldAlert",
  pagePath: "/RawMaterialIdentificationPage"
}, {
  id: "solvent-recovery",
  title: "HPLC Solvent Recovery Solutions",
  category: "Lab Sustainability",
  description: "Automated solvent recycling systems for analytical chromatography workflows, lowering hazardous waste disposal and solvent spend.",
  highlights: "Significant solvent reduction & cost savings",
  iconName: "Settings2",
  pagePath: "/HplcSolventRecoveryResolvePage"
}];
const categories = ["All Services", "Compliance & Assurance", "In-Line Monitoring", "Lifecycle Support", "Physical Characterization", "Quality Control", "Lab Sustainability"];
function renderServiceIcon(name: string) {
  const iconProps = {
    className: "size-5 text-primary",
    "aria-hidden": true
  };
  switch (name) {
    case "FileCheck2":
      return <FileCheck2 {...iconProps} />;
    case "Activity":
      return <Activity {...iconProps} />;
    case "Wrench":
      return <Wrench {...iconProps} />;
    case "FlaskConical":
      return <FlaskConical {...iconProps} />;
    case "ShieldAlert":
      return <ShieldAlert {...iconProps} />;
    case "Settings2":
      return <Settings2 {...iconProps} />;
    default:
      return <Gauge {...iconProps} />;
  }
}
export default function SupportServices() {
  const [selectedCategory, setSelectedCategory] = useState<string>("All Services");
  const prefersReducedMotion = useReducedMotion();
  const filteredServices = selectedCategory === "All Services" ? servicesData : servicesData.filter(item => item.category === selectedCategory);
  return <section id="industrysupportservices" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-3xl mb-12">
          <Badge variant="secondary" className="mb-4">
            Technical Support & Lifecycle Services
          </Badge>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-foreground mb-4">
            Specialized support for regulated laboratories and manufacturing
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            From instrument qualification to ongoing method support and preventative maintenance,
            we ensure your analytical workflows remain compliant, accurate, and productive.
          </p>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap gap-2 mb-10">
          {categories.map((category, index) => {
          const isActive = selectedCategory === category;
          return <div key={category} data-index={index}>
                <Button variant={isActive ? "default" : "outline"} size="sm" onClick={() => setSelectedCategory(category)} className="rounded-full text-xs font-medium cursor-pointer">
                  {category}
                </Button>
              </div>;
        })}
        </div>

        {/* Service Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredServices.map((service, index) => <motion.div key={service.id} data-index={index} initial={prefersReducedMotion ? false : {
          opacity: 0,
          y: 15
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          delay: index * 0.05
        }} className="flex">
              <Card className="flex flex-col justify-between w-full border border-border bg-card shadow-xs hover:shadow-md transition-shadow duration-200">
                <CardHeader className="space-y-3 pb-4">
                  <div className="flex items-center justify-between gap-2">
                    <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      {renderServiceIcon(service.iconName)}
                    </div>
                    <Badge variant="outline" className="text-xs text-muted-foreground">
                      {service.category}
                    </Badge>
                  </div>
                  <CardTitle className="text-xl font-semibold text-card-foreground line-clamp-2">
                    {service.title}
                  </CardTitle>
                  <CardDescription className="text-muted-foreground text-sm line-clamp-3">
                    {service.description}
                  </CardDescription>
                </CardHeader>

                <CardContent className="pt-0 pb-4">
                  <div className="flex items-start gap-2 pt-3 border-t border-border/60">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span className="text-xs text-muted-foreground font-medium">
                      {service.highlights}
                    </span>
                  </div>
                </CardContent>

                <CardFooter className="pt-0">
                  <Button asChild variant="outline" className="w-full justify-between group">
                    <Link to={service.pagePath}>
                      <span>Explore Service</span>
                      <ArrowUpRight className="size-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </div>

        {/* Bottom CTA Banner */}
        <div className="mt-14 p-6 md:p-8 rounded-xl bg-secondary/70 border border-border flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="space-y-1">
            <h3 className="text-lg font-semibold text-secondary-foreground">
              Need custom validation protocols or preventative service contracts?
            </h3>
            <p className="text-sm text-muted-foreground">
              Speak with our field application engineers across Ireland and the UK.
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0 w-full sm:w-auto">
            <Button asChild className="w-full sm:w-auto">
              <Link to="/ContactPage">Contact Service Team</Link>
            </Button>
            <Button asChild variant="outline" className="w-full sm:w-auto">
              <Link to="/SolutionsPage">View Solutions</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}